"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapTrackingPurchaseRevenueItems = exports.parseGtmCookieValues = exports.normalizeGclid = exports.GA_MEASUREMENT_ID = void 0;
const clubOffers_1 = require("../clubOffers");
const stripe_1 = require("../stripe");
const pricing_1 = require("../pricing");
const lodash_1 = require("lodash");
exports.GA_MEASUREMENT_ID = 'G-0LR2KJKD3M';
/**
 * Normalizes a GCLID value by extracting the raw click ID from the _gcl_aw cookie format.
 * The _gcl_aw cookie stores values in format: GCL.<timestamp>.<gclid>
 * This function extracts just the <gclid> portion, or returns the value as-is if already raw.
 *
 * @param raw - The GCLID value (may be raw or in _gcl_aw cookie format)
 * @returns The raw GCLID or null if input is null/undefined
 *
 * @example
 * normalizeGclid('GCL.1749160969.CjwKCAjw...') // returns 'CjwKCAjw...'
 * normalizeGclid('CjwKCAjw...') // returns 'CjwKCAjw...' (already raw)
 * normalizeGclid(null) // returns null
 */
const normalizeGclid = (raw) => {
    if (!raw)
        return null;
    let value = raw.trim();
    if (!value)
        return null;
    // Cookies may be URL encoded; query params from URLSearchParams are already decoded,
    // but decoding again is harmless for normal strings.
    try {
        if (value.includes('%'))
            value = decodeURIComponent(value);
    }
    catch {
        // no-op: keep original value if decoding fails
    }
    const match = value.match(/GCL\.\d+\.(.+)$/);
    return (match && match.length > 1 && match[1]) || value;
};
exports.normalizeGclid = normalizeGclid;
const parseGtmCookieValues = (clientId, sessionId, gclid, fbp) => {
    // Parse client ID, e.g. `GA1.2.1574969204.1715635974` => `1574969204.1715635974`
    const parsedClientIdMatch = clientId ? (clientId).match(/GA\d+\.\d+\.(\d+\.\d+)$/) : [];
    // Parse session ID, e.g. `GS2.1.s1746732205$o1$g0$t1746732219$j46$l0$h0` => `1746732205`
    const parsedSessionIdMatch = sessionId ? (sessionId).match(/GS\d+\.\d+\.s(\d+)/) : [];
    // Parse gcl ID, e.g. `GCL.1749160969.CjwKCAjw3f_BBhAPEiwAaA3K5FfzcqHDRNt8Es39aw16U8F3lGj2mtn4cHCW8o-YHc3WosAO8ncv7hoC0s0QAvD_BwE` => `CjwKCAjw3f_BBhAPEiwAaA3K5FfzcqHDRNt8Es39aw16U8F3lGj2mtn4cHCW8o-YHc3WosAO8ncv7hoC0s0QAvD_BwE`
    const parsedGclidMatch = gclid ? (gclid).match(/GCL\.\d+\.(.+)$/) : [];
    // Parse fbp, e.g. `fb.1.1596403881668.1116446470` => `1596403881668.1116446470`
    const parsedFbpMatch = fbp ? (fbp).match(/fb\.\d+\.(\d+\.\d+)$/) : [];
    return {
        parsedClientId: parsedClientIdMatch.length > 1 ? parsedClientIdMatch[1] : null,
        parsedSessionId: parsedSessionIdMatch.length > 1 ? parsedSessionIdMatch[1] : null,
        parsedGclid: parsedGclidMatch.length > 1 ? parsedGclidMatch[1] : null,
        parsedFbp: parsedFbpMatch.length > 1 ? parsedFbpMatch[1] : null,
    };
};
exports.parseGtmCookieValues = parseGtmCookieValues;
const mapTrackingPurchaseRevenueItems = (payment) => {
    const formatItemStrValue = (name) => {
        return (name || 'Misc').replace(/'/g, '');
    };
    const checkForClubOffer = (productId) => {
        for (const clubKey of (0, lodash_1.keys)(clubOffers_1.CLUB_OFFER_PLAN_INFO)) {
            if (clubOffers_1.CLUB_OFFER_PLAN_INFO[clubKey].productId === productId) {
                return clubOffers_1.CLUB_OFFER_PLAN_INFO[clubKey].club;
            }
        }
        return null;
    };
    // Gather metrics
    let club = null;
    const mappedItems = [];
    // Try to get a description from various sources
    const genericDescription = payment.description ||
        (0, lodash_1.get)(payment, 'items[0].description') ||
        (0, lodash_1.get)(payment, 'charges.data[0].description') ||
        null;
    if ((0, lodash_1.get)(payment, 'items.length')) {
        payment.items.forEach((item) => {
            const productId = (0, lodash_1.get)(item.price, 'product') || null;
            if (productId) {
                const productDetails = stripe_1.PRODUCT_DETAILS_MAP[productId];
                club = club || checkForClubOffer(productId);
                mappedItems.push({
                    index: mappedItems.length,
                    item_id: productId,
                    item_name: formatItemStrValue((0, lodash_1.get)(productDetails, 'name') || item.description),
                    discount: item.amount_discount ? (0, pricing_1.centsToDollars)(item.amount_discount) : 0,
                    item_category: formatItemStrValue((0, lodash_1.get)(productDetails, 'category')),
                    price: (0, pricing_1.centsToDollars)(item.amount_total),
                    quantity: item.quantity || 1,
                });
            }
        });
    }
    else {
        (payment.prices || []).forEach(({ _path, __ref__ }) => {
            // __ref__ is syntax from an onWrite listener function handler, _path is syntax from a regular collection query
            let productId = null;
            if (_path) {
                productId = (0, lodash_1.get)(_path, 'segments[1]') || null;
            }
            else if (__ref__) {
                productId = (0, lodash_1.get)(__ref__.split('/'), '[1]') || null;
            }
            if (productId) {
                const productDetails = stripe_1.PRODUCT_DETAILS_MAP[productId];
                club = club || checkForClubOffer(productId);
                mappedItems.push({
                    index: mappedItems.length,
                    item_id: productId,
                    item_name: formatItemStrValue((0, lodash_1.get)(productDetails, 'name') || genericDescription),
                    discount: 0,
                    item_category: formatItemStrValue((0, lodash_1.get)(productDetails, 'category')),
                    price: (0, pricing_1.centsToDollars)(payment.amount),
                    quantity: 1,
                });
            }
        });
    }
    if (!mappedItems.length) {
        console.log('No line items on the payment');
        mappedItems.push({
            index: mappedItems.length,
            item_id: formatItemStrValue(null),
            item_name: formatItemStrValue(genericDescription),
            discount: 0,
            item_category: formatItemStrValue(null),
            price: (0, pricing_1.centsToDollars)(payment.amount),
            quantity: 1,
        });
    }
    return {
        mappedItems,
        club,
        createdTimestampMillis: Math.round(((0, lodash_1.get)(payment, 'charges.data[0].created') || (0, lodash_1.get)(payment, 'created')) * 1000),
    };
};
exports.mapTrackingPurchaseRevenueItems = mapTrackingPurchaseRevenueItems;
